import React, { useState, useRef, useEffect } from "react";
import { Box, Button } from "@material-ui/core";
import { GetApp, CameraAlt, RotateRight } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface CameraCaptureProps {
  onCaptureComplete: (dataUrl: string) => void;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ onCaptureComplete }) => {
  const { t } = useTranslation();
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [rotation, setRotation] = useState<number>(0);
  const [countdown, setCountdown] = useState<number>(5);
  const [isCountingDown, setIsCountingDown] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const originalImageRef = useRef<string | null>(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 400, height: 300 },
      });
      streamRef.current = mediaStream;

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Could not access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    startCamera();

    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCountingDown && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isCountingDown && countdown === 0) {
      actualCapturePhoto();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountingDown, countdown]);

  const actualCapturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL("image/png");
    originalImageRef.current = dataUrl;
    setCapturedImage(dataUrl);
    setRotation(0);
    onCaptureComplete(dataUrl);
    stopCamera();
    setIsCountingDown(false);
    setCountdown(5);
  };

  const capturePhoto = () => {
    setIsCountingDown(true);
    setCountdown(5);
  };

  const retake = () => {
    setCapturedImage(null);
    originalImageRef.current = null;
    setRotation(0);
    setIsCountingDown(false);
    setCountdown(5);
    onCaptureComplete("");
    startCamera();
  };

  const handleRotate = () => {
    if (!originalImageRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      const newRotation = (rotation + 90) % 360;

      // Set canvas dimensions based on rotation
      if (newRotation === 90 || newRotation === 270) {
        canvas.width = img.height;
        canvas.height = img.width;
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();

      // Apply rotation transformation
      if (newRotation === 90) {
        ctx.translate(canvas.width, 0);
        ctx.rotate(Math.PI / 2);
      } else if (newRotation === 180) {
        ctx.translate(canvas.width, canvas.height);
        ctx.rotate(Math.PI);
      } else if (newRotation === 270) {
        ctx.translate(0, canvas.height);
        ctx.rotate(-Math.PI / 2);
      }

      ctx.drawImage(img, 0, 0);
      ctx.restore();

      const rotatedDataUrl = canvas.toDataURL("image/png");
      setCapturedImage(rotatedDataUrl);
      setRotation(newRotation);
      onCaptureComplete(rotatedDataUrl);
    };
    // Always rotate from the original image
    img.src = originalImageRef.current;
  };

  const handleSaveCapture = () => {
    if (!capturedImage) return;

    const link = document.createElement("a");
    link.href = capturedImage;
    link.download = `capture-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="center"
        mb={1}
        border="1px solid #ccc"
        borderRadius={4}
        p={1}
        bgcolor="#000"
        position="relative"
      >
        {!capturedImage ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: "400px",
                height: "300px",
                borderRadius: "4px",
              }}
            />
            {isCountingDown && countdown > 0 && <CountdownOverlay>{countdown}</CountdownOverlay>}
          </>
        ) : (
          <img
            src={capturedImage}
            alt="Captured"
            style={{
              maxWidth: "400px",
              maxHeight: "300px",
              borderRadius: "4px",
            }}
          />
        )}
      </Box>
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {!capturedImage ? (
        <Button
          variant="contained"
          color="primary"
          onClick={capturePhoto}
          fullWidth
          startIcon={<CameraAlt />}
          disabled={isCountingDown}
        >
          {t("imageGenerator.capturePhoto")}
        </Button>
      ) : (
        <Box display="flex" flexWrap="wrap">
          <Box mr={1} mb={1}>
            <Button variant="outlined" onClick={retake} startIcon={<CameraAlt />}>
              {t("imageGenerator.retakePhoto")}
            </Button>
          </Box>
          <Box mr={1} mb={1}>
            <Button variant="outlined" onClick={handleSaveCapture} startIcon={<GetApp />}>
              {t("imageGenerator.downloadImage")}
            </Button>
          </Box>
          <Box mb={1}>
            <Button variant="outlined" onClick={handleRotate} startIcon={<RotateRight />}>
              {t("imageGenerator.rotateImage")}
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

const CountdownOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  font-size: 120px;
  font-weight: bold;
  color: white;
  text-shadow: 0 0 20px rgba(0, 0, 0, 0.8);
  animation: pulse 1s ease-in-out;

  @keyframes pulse {
    0% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

export default CameraCapture;
